import { ConditionalRenderer } from '@/components/conditional-renderer';
import { SwitchRenderer } from '@/components/switch-renderer';
import { ErrorBoundary } from '@/error-boundary';
import { FallbackProps } from '@/error-boundary/contract';
import { NodeContext } from '@/node-editor';
import {
  Appender,
  NodeContainer,
  NodeControls,
  MigrationWarning,
  NodeWarning,
} from '@/node-editor/components';
import {
  getSelectionWithChildren,
  useSelectors,
} from '@/node-editor/store/selectors';
import { getTemplate } from '@/node-editor/utilities';
import { nodeMap } from '@/nodes';
import { INode, TRenderContext } from '@/types/node';
import React, { useMemo } from 'react';

interface IProps {
  node: INode;
  context: TRenderContext;
  conferenceId: string;
  pageId: string;
}

interface IRenderFallbackProps extends FallbackProps {
  conferenceId: string;
}

const EditFallback = ({
  boundaryReset,
  conferenceId,
  error,
}: IRenderFallbackProps): JSX.Element | null => {
  const node = error.getNode();

  return (
    <NodeControls node={node} template={getTemplate(node.type)} error>
      <SwitchRenderer
        switchCase={error.name}
        render={{
          NodeContentError: () =>
            error.hasMigration() ? (
              <MigrationWarning reset={boundaryReset} error={error} />
            ) : (
              <NodeWarning reset={boundaryReset} error={error} />
            ),
          NodeTypeError: () => (
            <NodeWarning reset={boundaryReset} error={error} />
          ),
          NodeNotFoundError: () => (
            <NodeWarning reset={boundaryReset} error={error} />
          ),
          default: () => null,
        }}
      />
      {React.cloneElement(
        error.data[error.getContext()]({
          node,
          conferenceId,
          children: node.children || [],
        })
      )}
    </NodeControls>
  );
};

const Node = React.memo(({ node, context, conferenceId, pageId }: IProps) => {
  const Component = useMemo(() => {
    // Create a stable provided object to prevent re-creation
    const provided = { node, context };
    return nodeMap[node.type](provided);
  }, [node.type, node.id, context]);

  const _quickhackPassCardOrAreaTitleSlotProp = useMemo(():
    | string
    | undefined => {
    switch (node.type) {
      case 'card': {
        return node.content?.text;
      }
      case 'widget-area': {
        return node.content?.areaName;
      }
      default:
        return undefined;
    }
  }, [node.type, node.content?.text, node.content?.areaName]);

  return (
    <NodeControls
      node={node}
      template={getTemplate(node.type)}
      slot={_quickhackPassCardOrAreaTitleSlotProp}
    >
      {({ supportsChildren, reorderNode }) => (
        <ConditionalRenderer
          condition={supportsChildren}
          render={(children) => (
            <NodeContainer
              id={node.id}
              onDrop={({ source, destination }) =>
                reorderNode(node.id, source.index, destination.index)
              }
            >
              {children}
              <Appender nodeId={node.id} />
            </NodeContainer>
          )}
        >
          <Component
            key={`${node.id}-${node.type}`}
            node={node}
            context={context}
            conferenceId={conferenceId}
            pageId={pageId}
          >
            {node.children as INode[]}
          </Component>
        </ConditionalRenderer>
      )}
    </NodeControls>
  );
});

export const EditChildren = React.memo(
  ({ children }: { children: INode[] }) => {
    const selectors = useSelectors(() => ({
      getSelectionWithChildren,
    }));

    const selectedChildren = useMemo(
      () => selectors.getSelectionWithChildren(children),
      [selectors, children]
    );

    return (
      <NodeContext.Consumer>
        {({ context, conferenceId, pageId }) =>
          selectedChildren.map((node) => (
            <ErrorBoundary
              key={node.id}
              fallbackRender={(fallbackProps) => (
                <EditFallback conferenceId={conferenceId} {...fallbackProps} />
              )}
            >
              <Node
                node={node}
                context={context}
                conferenceId={conferenceId}
                pageId={pageId}
              />
            </ErrorBoundary>
          ))
        }
      </NodeContext.Consumer>
    );
  }
);
